import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ReplayIcon from "@material-ui/icons/Replay";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import "./Schedules.css";

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const localizer = momentLocalizer(moment);

const defaultMessages = {
  date: i18n.t("schedules.date"),
  time: i18n.t("schedules.time"),
  event: i18n.t("schedules.event"),
  allDay: i18n.t("schedules.allDay"),
  week: i18n.t("schedules.week"),
  work_week: i18n.t("schedules.work_week"),
  day: i18n.t("schedules.day"),
  month: i18n.t("schedules.month"),
  previous: i18n.t("schedules.previous"),
  next: i18n.t("schedules.next"),
  yesterday: i18n.t("schedules.yesterday"),
  tomorrow: i18n.t("schedules.tomorrow"),
  today: i18n.t("schedules.today"),
  agenda: i18n.t("schedules.agenda"),
  noEventsInRange: i18n.t("schedules.noEventsInRange"),
  showMore: (total) => `+${total} mais`,
};

const STATUS_META = {
  PENDENTE: { label: "Pendente", color: "#f0ad4e", bg: "#fff3cd" },
  AGENDADA: { label: "Agendada", color: "#5bc0de", bg: "#d1ecf1" },
  ENVIADA: { label: "Enviada", color: "#5cb85c", bg: "#d4edda" },
  ERRO: { label: "Erro", color: "#d9534f", bg: "#f8d7da" },
  CANCELADA: { label: "Cancelada", color: "#777", bg: "#e2e3e5" },
};

const normalizeStatus = (s) => {
  if (!s) return "PENDENTE";
  const up = String(s).toUpperCase();
  return STATUS_META[up] ? up : "PENDENTE";
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const incoming = action.payload;
    const next = [...state];
    incoming.forEach((sched) => {
      const idx = next.findIndex((s) => s.id === sched.id);
      if (idx !== -1) next[idx] = sched;
      else next.push(sched);
    });
    return next;
  }
  if (action.type === "UPDATE_SCHEDULES") {
    const sched = action.payload;
    const idx = state.findIndex((s) => s.id === sched.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = sched;
      return next;
    }
    return [sched, ...state];
  }
  if (action.type === "DELETE_SCHEDULE") {
    return state.filter((s) => s.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1.5),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filtersBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  dateField: {
    width: 160,
  },
  statusChip: {
    fontWeight: 600,
    height: 22,
    minWidth: 88,
  },
  statusDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 8,
    verticalAlign: "middle",
  },
  msgPreview: {
    maxWidth: 340,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  smallAvatar: {
    width: 28,
    height: 28,
    fontSize: 12,
  },
  emptyRow: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
  loadingRow: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  calendarToolbar: {
    "& .rbc-toolbar-label": {
      color: theme.mode === "light" ? theme.palette.light : "white",
    },
    "& .rbc-btn-group button": {
      color: theme.mode === "light" ? theme.palette.light : "white",
      "&:hover": { color: theme.palette.mode === "dark" ? "#fff" : "#000" },
      "&:active": { color: theme.palette.mode === "dark" ? "#fff" : "#000" },
      "&:focus": { color: theme.palette.mode === "dark" ? "#fff" : "#000" },
      "&.rbc-active": { color: theme.palette.mode === "dark" ? "#fff" : "#000" },
    },
  },
  tabsRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },
  legendBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  legendLabel: {
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
  },
}));

const StatusChip = ({ status, classes }) => {
  const key = normalizeStatus(status);
  const meta = STATUS_META[key];
  return (
    <Chip
      size="small"
      label={meta.label}
      className={classes.statusChip}
      style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.color }}
      variant="outlined"
    />
  );
};

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tab, setTab] = useState(0); // 0=Lista, 1=Calendário
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" = todos
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().add(30, "days").format("YYYY-MM-DD")
  );
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => history.push(`/`), 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const params = { searchParam, pageNumber };
      if (statusFilter) params.status = statusFilter;
      if (startDate) params.startDate = `${startDate}T00:00:00`;
      if (endDate) params.endDate = `${endDate}T23:59:59`;
      const { data } = await api.get("/schedules", { params });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setCount(data.count);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, pageNumber, statusFilter, startDate, endDate]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, startDate, endDate]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => fetchSchedules(), 400);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, statusFilter, startDate, endDate, fetchSchedules]);

  useEffect(() => {
    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };
    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);
    return () => socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
  }, [socket, user.companyId]);

  const cleanContact = () => setContactId("");

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    dispatch({ type: "DELETE_SCHEDULE", payload: scheduleId });
  };

  const handleReenviar = async (schedule) => {
    try {
      await api.put(`/schedules/${schedule.id}`, {
        ...schedule,
        status: "PENDENTE",
        sentAt: null,
      });
      toast.success("Agendamento reenfileirado.");
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => setPageNumber((p) => p + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const openRowMenu = (event, sched) => {
    setMenuAnchor(event.currentTarget);
    setMenuTarget(sched);
  };
  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const isSent = (s) => Boolean(s?.sentAt) || normalizeStatus(s?.status) === "ENVIADA";

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const calendarEvents = useMemo(
    () =>
      schedules.map((sched) => {
        const start = new Date(sched.sendAt);
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        return {
          id: sched.id,
          title: sched?.contact?.name || "?",
          start,
          end,
          resource: sched,
        };
      }),
    [schedules]
  );

  const eventPropGetter = useCallback((event) => {
    const status = normalizeStatus(event.resource?.status);
    const meta = STATUS_META[status];
    return {
      style: {
        backgroundColor: meta.color,
        color: "#fff",
        border: "none",
        borderLeft: `3px solid ${meta.color}`,
        fontSize: 12,
        opacity: 0.92,
      },
    };
  }, []);

  const CalendarEvent = ({ event }) => {
    const sched = event.resource;
    const status = normalizeStatus(sched?.status);
    const meta = STATUS_META[status];
    return (
      <Tooltip
        title={
          <div style={{ maxWidth: 320 }}>
            <div style={{ fontWeight: 600 }}>
              {sched?.contact?.name || "?"} — {meta.label}
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              {moment(sched.sendAt).format("DD/MM/YYYY HH:mm")}
              {sched?.whatsapp?.name ? ` · ${sched.whatsapp.name}` : ""}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, whiteSpace: "pre-wrap" }}>
              {String(sched?.body || "").substring(0, 200)}
              {String(sched?.body || "").length > 200 ? "…" : ""}
            </div>
          </div>
        }
        arrow
        placement="top"
      >
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 12,
            padding: "2px 4px",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleEditSchedule(sched);
          }}
        >
          {sched?.contact?.name || "?"}
        </div>
      </Tooltip>
    );
  };

  const statusOptions = ["", "PENDENTE", "AGENDADA", "ENVIADA", "ERRO", "CANCELADA"];

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
          viewOnly={isSent(selectedSchedule)}
        />
      )}
      <MainHeader>
        <Title>
          {i18n.t("schedules.title")} ({count})
        </Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={tab === 0 ? handleScroll : undefined}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsRoot}
        >
          <Tab label="Lista" />
          <Tab label="Calendário" />
        </Tabs>

        <div className={classes.filtersBar}>
          <div className={classes.chipsRow}>
            {statusOptions.map((s) => {
              const isAll = s === "";
              const active = statusFilter === s;
              const meta = isAll ? null : STATUS_META[s];
              return (
                <Chip
                  key={s || "all"}
                  size="small"
                  icon={
                    isAll ? undefined : (
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: meta.color, marginLeft: 8, marginRight: -4 }}
                      />
                    )
                  }
                  label={isAll ? "Todos" : meta.label}
                  clickable
                  onClick={() => setStatusFilter(s)}
                  variant={active ? "default" : "outlined"}
                  style={
                    active && meta
                      ? { backgroundColor: meta.color, color: "#fff", borderColor: meta.color }
                      : {}
                  }
                />
              );
            })}
          </div>
          <div style={{ flex: 1 }} />
          <TextField
            label="De"
            type="date"
            size="small"
            className={classes.dateField}
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            className={classes.dateField}
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className={classes.legendBar}>
          <span className={classes.legendLabel}>Legenda:</span>
          {["PENDENTE", "AGENDADA", "ENVIADA", "ERRO", "CANCELADA"].map((s) => {
            const meta = STATUS_META[s];
            return (
              <span key={s} className={classes.legendItem}>
                <span
                  className={classes.statusDot}
                  style={{ backgroundColor: meta.color, marginRight: 4 }}
                />
                {meta.label}
              </span>
            );
          })}
        </div>

        {tab === 0 && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 260 }}>Contato</TableCell>
                <TableCell style={{ width: 140 }}>Quando</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell style={{ width: 140 }}>Conexão</TableCell>
                <TableCell style={{ width: 120 }}>Status</TableCell>
                <TableCell style={{ width: 60 }} align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyRow}>
                    Nenhum agendamento no intervalo selecionado.
                  </TableCell>
                </TableRow>
              )}
              {schedules.map((sched) => {
                const status = normalizeStatus(sched.status);
                const preview = String(sched.body || "").replace(/\s+/g, " ").trim();
                return (
                  <TableRow key={sched.id} hover>
                    <TableCell>
                      <div className={classes.contactCell}>
                        <Avatar
                          src={sched?.contact?.urlPicture}
                          className={classes.smallAvatar}
                        >
                          {initials(sched?.contact?.name)}
                        </Avatar>
                        <span>{sched?.contact?.name || "?"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {moment(sched.sendAt).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={preview} arrow placement="top">
                        <div className={classes.msgPreview}>{preview}</div>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {sched?.whatsapp?.name ? (
                        <Chip
                          size="small"
                          label={sched.whatsapp.name}
                          style={{
                            backgroundColor: sched.whatsapp.color || "#eee",
                            color: "#fff",
                            maxWidth: 130,
                          }}
                        />
                      ) : (
                        <span style={{ color: "#888" }}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={status} classes={classes} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => openRowMenu(e, sched)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.loadingRow}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {tab === 1 && (
          <Calendar
            messages={defaultMessages}
            formats={{
              agendaDateFormat: "DD/MM ddd",
              weekdayFormat: "dddd",
            }}
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 620, marginTop: 8 }}
            className={classes.calendarToolbar}
            eventPropGetter={eventPropGetter}
            components={{ event: CalendarEvent }}
            min={new Date(0, 0, 0, 7, 0, 0)}
            max={new Date(0, 0, 0, 21, 0, 0)}
            step={30}
            timeslots={2}
            popup
            defaultView="week"
          />
        )}
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
      >
        <MenuItem
          onClick={() => {
            handleEditSchedule(menuTarget);
            closeRowMenu();
          }}
        >
          {isSent(menuTarget) ? (
            <>
              <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
              Ver
            </>
          ) : (
            <>
              <EditIcon fontSize="small" style={{ marginRight: 8 }} />
              Editar
            </>
          )}
        </MenuItem>
        {normalizeStatus(menuTarget?.status) === "ERRO" && (
          <MenuItem
            onClick={() => {
              handleReenviar(menuTarget);
              closeRowMenu();
            }}
          >
            <ReplayIcon fontSize="small" style={{ marginRight: 8 }} />
            Reenviar
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDeletingSchedule(menuTarget);
            setConfirmModalOpen(true);
            closeRowMenu();
          }}
          style={{ color: "#d9534f" }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          Excluir
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default Schedules;
